import { ApiResponse } from "../../types/api";
import type {
  FHIRMedicationBundle,
  MedicationApiResponse,
  MedicationFormData,
  MedicationImageUploadResponse,
  MedicationListApiResponse,
  MedicationStatsApiResponse,
  MedicationTerminologies,
  TerminologiesApiResponse,
} from "../../types/medication";
import { apiHelpers } from "../api";

export interface Medication {
  id: string;
  name: string;
  genericName: string | null | undefined;
  activeIngredient: string | null | undefined;
  purpose: string | null | undefined;
  dosage: string;
  dosageForm: string | null | undefined;
  strength: string | null | undefined;
  strengthUnit: string | null | undefined;
  frequency: string;
  mealTiming: string | null | undefined;
  startDate: string;
  endDate: string | null | undefined;
  prescribedBy: string | null | undefined;
  pharmacy: string | null | undefined;
  instructions: string | null | undefined;
  eanCode: string | null | undefined;
  pznCode: string | null | undefined;
  atcCode: string | null | undefined;
  snomedCode: string | null | undefined;
  medicationType: string | null | undefined;
  travelCategory: string | null | undefined;
  isActive: boolean;
}

export interface FHIRMedication {
  resourceType: "Medication";
  id: string;
  identifier: Array<{
    use: string;
    system: string;
    value: string;
  }>;
  code: {
    coding: Array<{
      system: string;
      code: string;
      display: string;
    }>;
    text: string;
  };
  status: "active" | "inactive";
  form?: {
    coding: Array<{
      system: string;
      code: string;
      display: string;
    }>;
  };
  ingredient?: Array<{
    itemCodeableConcept: {
      text: string;
    };
    strength?: {
      numerator: {
        value: number;
        unit: string;
      };
    };
  }>;
  extension: Array<{
    url: string;
    valueString: string;
  }>;
  _healthpass: {
    dosage: string;
    frequency: string;
    startDate: string;
    endDate?: string;
    prescribedBy?: string;
    pharmacy?: string;
    instructions?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface FHIRBundle {
  resourceType: "Bundle";
  type: "collection";
  entry: Array<{
    resource: FHIRMedication;
  }>;
}

export interface CreateMedicationRequest {
  name: string;
  activeIngredient?: string;
  purpose?: string;
  dosage: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  prescribedBy?: string;
  pharmacy?: string;
  instructions?: string;
  medicationType?: string;
  travelCategory?: string;
}

export interface UpdateMedicationRequest
  extends Partial<CreateMedicationRequest> {}

class MedicationService {
  private buildMeta(resourceType: string): {
    fhirVersion: string;
    resourceType: string;
    timestamp: string;
  } {
    return {
      fhirVersion: "R5",
      resourceType,
      timestamp: new Date().toISOString(),
    };
  }

  private getFallbackTerminologies(): MedicationTerminologies {
    return {
      snomedCodes: {},
      categoryCodes: {},
      timingCodes: {},
      ucumUnits: {},
      medicationStatementStatus: {
        recorded: "Recorded",
        active: "Active",
        completed: "Completed",
        stopped: "Stopped",
        draft: "Draft",
        "entered-in-error": "Entered in Error",
      },
      packageSizes: [
        { code: "N1", display: "N1" },
        { code: "N2", display: "N2" },
        { code: "N3", display: "N3" },
        { code: "SAMPLE", display: "Sample" },
        { code: "CUSTOM", display: "Custom" },
      ],
      prescriptionPurposes: [],
      intakeTimings: [
        { code: "morning", display: "Morning", time: "08:00" },
        { code: "noon", display: "Noon", time: "12:00" },
        { code: "afternoon", display: "Afternoon", time: "16:00" },
        { code: "evening", display: "Evening", time: "20:00" },
        { code: "night", display: "Night", time: "23:00" },
        { code: "bedtime", display: "Bedtime", time: "22:00" },
      ],
      mealTimings: [],
      prescriptionCountries: [],
      doseForms: {},
      dosageUnits: {
        european: [
          {
            system: "http://unitsofmeasure.org",
            code: "mg",
            display: "Milligram",
            displayShort: "mg",
          },
          {
            system: "http://unitsofmeasure.org",
            code: "g",
            display: "Gram",
            displayShort: "g",
          },
          {
            system: "http://unitsofmeasure.org",
            code: "mL",
            display: "Milliliter",
            displayShort: "ml",
          },
          {
            system: "http://unitsofmeasure.org",
            code: "tablet",
            display: "Tablet",
            displayShort: "tbl",
          },
        ],
        us: [],
        legacy: [
          { system: "http://unitsofmeasure.org", code: "mg", display: "mg" },
          { system: "http://unitsofmeasure.org", code: "g", display: "g" },
          { system: "http://unitsofmeasure.org", code: "mL", display: "ml" },
        ],
      },
    };
  }

  private mapResourceToBundle(resourceRow: any): FHIRMedicationBundle {
    const rawData = resourceRow?.data ?? resourceRow;
    const medicationStatement = rawData?.medicationStatement ?? rawData;
    const medication = rawData?.medication ?? null;
    const normalizedId =
      medicationStatement?.id || String(resourceRow?.id || "");

    return {
      medicationStatement: {
        ...medicationStatement,
        id: normalizedId,
        resourceType:
          medicationStatement?.resourceType || "MedicationStatement",
        subject: medicationStatement?.subject || {
          reference: "Patient/current",
        },
        status: medicationStatement?.status || "recorded",
      },
      medication,
    };
  }

  private buildListResponse(resources: any[]): MedicationListApiResponse {
    const entries = resources.map((row) => ({
      resource: this.mapResourceToBundle(row),
    }));

    return {
      success: true,
      data: {
        resourceType: "Bundle",
        type: "collection",
        total: entries.length,
        entry: entries,
      },
      meta: this.buildMeta("MedicationStatement"),
    };
  }

  private async fetchMedicationRows(): Promise<any[]> {
    const response = await apiHelpers.get<{
      success: boolean;
      data?: { medications?: any[] };
    }>("/fhir/medications");
    return response.data?.data?.medications || [];
  }

  /**
   * Get all medications for the authenticated user (FHIR R5)
   */
  async getMedications(): Promise<MedicationListApiResponse> {
    const rows = await this.fetchMedicationRows();
    return this.buildListResponse(rows);
  }

  /**
   * Get active medications (FHIR R5)
   */
  async getActiveMedications(): Promise<MedicationListApiResponse> {
    const rows = await this.fetchMedicationRows();
    const activeRows = rows.filter((row) => {
      const status =
        row?.data?.medicationStatement?.status || row?.data?.status;
      return status === "active" || status === "recorded";
    });
    return this.buildListResponse(activeRows);
  }

  /**
   * Get medication by ID (FHIR R5)
   */
  async getMedicationById(id: string): Promise<MedicationApiResponse> {
    const numericId = Number(id);

    if (!Number.isNaN(numericId)) {
      try {
        const response = await apiHelpers.get<{
          success: boolean;
          data?: { resource?: any };
        }>(`/fhir/resources/${numericId}`);
        const resource = response.data?.data?.resource;
        if (resource) {
          return {
            success: true,
            data: this.mapResourceToBundle(resource),
            meta: this.buildMeta("MedicationStatement"),
          };
        }
      } catch {
        // Fallback below
      }
    }

    const rows = await this.fetchMedicationRows();
    const found = rows.find((row) => {
      const statementId =
        row?.data?.medicationStatement?.id ||
        row?.data?.id ||
        String(row?.id || "");
      return String(statementId) === String(id);
    });

    if (!found) {
      throw new Error("Medication not found");
    }

    return {
      success: true,
      data: this.mapResourceToBundle(found),
      meta: this.buildMeta("MedicationStatement"),
    };
  }

  /**
   * Get medication statistics
   */
  async getMedicationStats(): Promise<MedicationStatsApiResponse> {
    const list = await this.getMedications();
    const items = list.data.entry.map((entry) => entry.resource);

    const active = items.filter((item) => {
      const status = item?.medicationStatement?.status;
      return status === "active" || status === "recorded";
    }).length;

    return {
      success: true,
      data: {
        total: items.length,
        active,
        expiring: 0,
        byCategory: {},
      },
      meta: this.buildMeta("MedicationStatement"),
    };
  }

  /**
   * Get FHIR terminologies and code systems
   */
  async getTerminologies(): Promise<TerminologiesApiResponse> {
    return {
      success: true,
      data: this.getFallbackTerminologies(),
      meta: this.buildMeta("Terminologies"),
    };
  }

  /**
   * Create a new medication (FHIR R5)
   */
  async createMedication(
    formData: MedicationFormData,
  ): Promise<MedicationApiResponse> {
    const fhirInput = this.convertFormDataToFHIR(formData);
    const response = await apiHelpers.post<{
      success: boolean;
      data?: { medication?: any };
    }>("/fhir/medications", fhirInput);
    const created = response.data?.data?.medication;

    return {
      success: true,
      data: this.mapResourceToBundle(created),
      meta: this.buildMeta("MedicationStatement"),
    };
  }

  /**
   * Update an existing medication (FHIR R5)
   */
  async updateMedication(
    id: string,
    formData: Partial<MedicationFormData>,
  ): Promise<MedicationApiResponse> {
    const fhirInput = this.convertFormDataToFHIR(formData);
    const response = await apiHelpers.put<{
      success: boolean;
      data?: { resource?: any };
    }>(`/fhir/resources/${id}`, {
      data: fhirInput,
    });
    const updated = response.data?.data?.resource;

    return {
      success: true,
      data: this.mapResourceToBundle(updated),
      meta: this.buildMeta("MedicationStatement"),
    };
  }

  /**
   * Delete a medication (FHIR R5)
   */
  async deleteMedication(
    id: string,
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    await apiHelpers.delete(`/fhir/resources/${id}`);
    return {
      success: true,
      data: { deleted: true },
    } as ApiResponse<{ deleted: boolean }>;
  }

  /**
   * Upload package image
   */
  async uploadPackageImage(
    medicationId: string,
    file: File,
  ): Promise<MedicationImageUploadResponse> {
    const formData = new FormData();
    formData.append("image", file);

    // Don't set Content-Type manually - let Axios set it with boundary
    const response = await apiHelpers.post<MedicationImageUploadResponse>(
      `/fhir/medications/${medicationId}/images/package`,
      formData,
    );
    return response.data;
  }

  /**
   * Upload medication/pill image
   */
  async uploadMedicationImage(
    medicationId: string,
    file: File,
  ): Promise<MedicationImageUploadResponse> {
    const formData = new FormData();
    formData.append("image", file);

    // Don't set Content-Type manually - let Axios set it with boundary
    const response = await apiHelpers.post<MedicationImageUploadResponse>(
      `/fhir/medications/${medicationId}/images/medication`,
      formData,
    );
    return response.data;
  }

  /**
   * Delete medication image
   */
  async deleteMedicationImage(
    medicationId: string,
    imageType: "package" | "medication",
  ): Promise<ApiResponse<unknown>> {
    const response = await apiHelpers.delete<ApiResponse<unknown>>(
      `/fhir/medications/${medicationId}/images/${imageType}`,
    );
    return response.data;
  }

  /**
   * Convert form data to FHIR R5 structure
   */
  private convertFormDataToFHIR(
    formData: Partial<MedicationFormData>,
  ): unknown {
    const medicationStatement: Record<string, unknown> = {
      resourceType: "MedicationStatement",
      status: formData.status || "recorded",
      medicationCodeableConcept: {
        text: formData.name || formData.tradeName || "Unknown Medication",
      },
      subject: {
        reference: "Patient/current", // Will be replaced by backend
      },
    };

    // Dosage detail
    if (formData.dosageValue && formData.dosageUnit) {
      medicationStatement.dosageDetail = {
        value: formData.dosageValue,
        unit: formData.dosageUnit,
        text: `${formData.dosageValue} ${formData.dosageUnit}`,
      };
    }

    // Intake timing
    if (formData.intakeTiming) {
      medicationStatement.intakeTiming = formData.intakeTiming;
    }

    // Intake instructions
    if (formData.intakeInstructions) {
      medicationStatement.intakeInstructions = formData.intakeInstructions;
    }

    // Medication period
    if (formData.startDate || formData.endDate) {
      medicationStatement.medicationPeriod = {
        start: formData.startDate,
        end: formData.endDate,
      };
      medicationStatement.effectivePeriod = {
        start: formData.startDate,
        end: formData.endDate,
      };
    }

    // Prescriber
    if (formData.prescriberName) {
      medicationStatement.prescriber = {
        name: formData.prescriberName,
        signature:
          formData.prescriberSignature === "self" ? "Self Signed" : undefined,
        signatureDate:
          formData.prescriberSignature === "self"
            ? new Date().toISOString()
            : undefined,
      };
    }

    // Prescription country
    if (formData.prescriptionCountry) {
      medicationStatement.prescriptionCountry = formData.prescriptionCountry;
    }

    // Documents
    if (formData.pharmacyCodeUrl || formData.prescriptionUrl) {
      medicationStatement.documents = {
        pharmacyCode: formData.pharmacyCodeUrl,
        prescription: formData.prescriptionUrl,
      };
    }

    // Medication resource (optional)
    const medication: Record<string, unknown> | null =
      formData.name || formData.tradeName
        ? {
            resourceType: "Medication",
            code: {
              text: formData.name || formData.tradeName,
            },
            status: "active",
          }
        : null;

    if (medication) {
      // Trade name
      if (formData.tradeName) {
        medication.tradeName = formData.tradeName;
      }

      // Active ingredient
      if (formData.activeIngredient) {
        medication.activeIngredient = formData.activeIngredient;
      }

      // Manufacturer
      if (formData.manufacturer) {
        medication.manufacturerName = formData.manufacturer;
      }

      // Package size
      if (formData.packageSize) {
        medication.packageSize = formData.packageSize;
      }

      // Prescription purpose
      if (formData.prescriptionPurpose) {
        medication.prescriptionPurpose = formData.prescriptionPurpose;
      }

      // Dose form
      if (formData.doseForm) {
        medication.doseForm = {
          coding: [
            {
              system: "http://snomed.info/sct",
              code: formData.doseForm,
              display: formData.doseForm,
            },
          ],
          text: formData.doseForm,
        };
      }

      // Images
      if (formData.packageImageUrl || formData.medicationImageUrl) {
        medication.images = {
          packageImage: formData.packageImageUrl,
          medicationImage: formData.medicationImageUrl,
        };
      }
    }

    return {
      medicationStatement,
      medication,
    };
  }

  /**
   * Generate international medication plan
   */
  async generateInternationalPlan(data: {
    destinations?: string[];
    travelDates?: {
      departure: string;
      return: string;
    };
    existingMedications?: string[];
  }): Promise<ApiResponse<unknown>> {
    const response = await apiHelpers.post<ApiResponse<unknown>>(
      "/medications/international-plan",
      data,
    );
    return response.data;
  }

  /**
   * Convert FHIR medication to frontend format
   */
  convertFHIRToMedication(fhirMed: FHIRMedication): Medication {
    const extensions = fhirMed.extension || [];
    const purposeExt = extensions.find((ext) =>
      ext.url.includes("medication-purpose"),
    );
    const typeExt = extensions.find((ext) =>
      ext.url.includes("medication-type"),
    );
    const travelCategoryExt = extensions.find((ext) =>
      ext.url.includes("travel-category"),
    );
    const mealTimingExt = extensions.find((ext) =>
      ext.url.includes("meal-timing"),
    );

    const activeIngredient = fhirMed.ingredient?.[0]?.itemCodeableConcept?.text;
    const strength =
      fhirMed.ingredient?.[0]?.strength?.numerator?.value?.toString();
    const strengthUnit = fhirMed.ingredient?.[0]?.strength?.numerator?.unit;

    return {
      id: fhirMed.id,
      name: fhirMed.code.text,
      genericName: fhirMed.code.text || null,
      activeIngredient: activeIngredient || null,
      purpose: purposeExt?.valueString || null,
      dosage: fhirMed._healthpass.dosage,
      dosageForm: fhirMed.form?.coding?.[0]?.display || null,
      strength: strength || null,
      strengthUnit: strengthUnit || null,
      frequency: fhirMed._healthpass.frequency,
      mealTiming: mealTimingExt?.valueString || null,
      startDate: fhirMed._healthpass.startDate,
      endDate: fhirMed._healthpass.endDate || null,
      prescribedBy: fhirMed._healthpass.prescribedBy || null,
      pharmacy: fhirMed._healthpass.pharmacy || null,
      instructions: fhirMed._healthpass.instructions || null,
      eanCode:
        fhirMed.identifier.find((id) => id.system.includes("gtin"))?.value ||
        null,
      pznCode:
        fhirMed.identifier.find((id) => id.system.includes("pzn"))?.value ||
        null,
      atcCode:
        fhirMed.identifier.find((id) => id.system.includes("atc"))?.value ||
        null,
      snomedCode: fhirMed.code.coding?.[0]?.code || null,
      medicationType: typeExt?.valueString || null,
      travelCategory: travelCategoryExt?.valueString || null,
      isActive: fhirMed.status === "active",
    };
  }

  /**
   * Convert FHIR Bundle to medication array
   */
  convertFHIRBundleToMedications(bundle: FHIRBundle): Medication[] {
    return bundle.entry.map((entry) =>
      this.convertFHIRToMedication(entry.resource),
    );
  }
}

export const medicationService = new MedicationService();
export default medicationService;
